import pako from 'pako';
/**
 * UniversalCanvasService is a service class that manages task data and history objects.
 * It provides methods to access task data, fetch context, and manage history objects.
 */
class UniversalCanvasService {
    /**
     * Constructor for UniversalCanvasService.
     * @param {Object} $logger - The logger service.
     * @param {Object} $injector - The injector service.
     */
    constructor($injector) {
        this._logIdentifier = 'UniversalCanvasService';

        this._TaskDataService = null;
        this._fetchContext = null;
        this._historyObjects = {};
        this.$injector = $injector;

        // Compression settings
        this.compressionActivate = true;
        this.compressionKeyword = "--COMPRESSION--";
        this.compressionOptions = {
            to: 'string',
            level: 9,
            gzip: true,
            header: { text: true }
        };

        // Font mapping for SVG processing
        this.oldFontNameList = [
            {
                oldFontFamily: 'font-family="Helvetica"',
                newFontFamily: 'font-family="UC_Helvetica"',
            },
            {
                oldFontFamily: 'font-family="Georgia"',
                newFontFamily: 'font-family="UC_Georgia"',
            },
            {
                oldFontFamily: 'font-family="UC_Times_New_Roman"',
                newFontFamily: 'font-family="UC_Helvetica"',
            },
            {
                oldFontFamily: 'font-family="Courier"',
                newFontFamily: 'font-family="UC_Courier"',
            },
            {
                oldFontFamily: 'font-family="Palatino"',
                newFontFamily: 'font-family="UC_Palatino"',
            },
        ];
    }

    /**
     * Get the TaskDataService instance.
     * @returns {Object} - The TaskDataService instance.
     */
    getTaskDataService() {
        if (!this._TaskDataService) {
            this._TaskDataService = this.$injector.get('TaskDataService');
        }
        return this._TaskDataService;
    }

    /**
     * Get the fetch context.
     * @returns {Object} - The fetch context.
     */
    getFetchContext() {
        if (!this._fetchContext) {
            this._fetchContext = this.getTaskDataService().fetchContext();
        }
        return this._fetchContext;
    }

    /**
     * Get the history object for a given UUID.
     * @param {string} objectUUID - The UUID of the object.
     * @returns {Object} - The history object.
     */
    getHistoryObject(objectUUID) {
        if (!this._historyObjects[objectUUID]) {
            this._historyObjects[objectUUID] = new History(objectUUID, this.getTaskDataService());
        }
        return this._historyObjects[objectUUID];
    }

    /**
     * Destroy the history object for a given UUID.
     * @param {string} objectUUID - The UUID of the object.
     */
    destroyHistoryObject(objectUUID) {
        if (this._historyObjects[objectUUID]) {
            delete this._historyObjects[objectUUID];
        }
    }

    /**
     * Compresses the attachment data.
     * @param {Object} attachement - The attachment to compress.
     * @returns {Object} The compressed attachment.
     */
    compression(attachement) {
        if (!this.compressionActivate) return attachement;
        if (typeof attachement === "string" && attachement.indexOf(this.compressionKeyword) !== -1) {
            attachement = this.decompression(attachement);
        }
        try {
            attachement = this.compressionKeyword + window.btoa(pako.deflate(JSON.stringify(attachement), this.compressionOptions));
        } catch (err) {
            console.log(err);
        }
        return attachement;
    }

    /**
     * Decompresses the attachment data.
     * @param {Object} attachement - The attachment to decompress.
     * @returns {Object} The decompressed attachment.
     */
    decompression(attachement) {
        if (!this.compressionActivate) return attachement;
        if (typeof attachement === "string" && attachement.indexOf(this.compressionKeyword) !== -1) {
            attachement = JSON.parse(pako.inflate(window.atob(attachement.replace(new RegExp(this.compressionKeyword, "g"), "")), this.compressionOptions));
        }
        return attachement;
    }

    /**
     * Adds Universal Canvas fonts to SVG content.
     * @param {string} svg - The SVG content.
     * @returns {string} The SVG content with updated fonts.
     */
    addUCFontsToSVG(svg) {
        if (svg) {
            this.oldFontNameList.forEach(item => {
                svg = svg.replace(new RegExp(item.oldFontFamily, 'g'), item.newFontFamily);
            });
        }
        return svg;
    }

    /**
     * Fixes media paths in SVG content.
     * @param {Object} attachement - The attachment containing SVG data.
     * @param {string} taskuuid - The task UUID.
     * @param {boolean} isCMSNotPreview - Whether in CMS not preview mode.
     * @param {boolean} isPDFMode - Whether in PDF mode.
     * @param {boolean} examExport - Whether in exam export mode.
     * @returns {Object} The attachment with fixed media paths.
     */
    fixSVGMediaPaths(attachement, { taskuuid, isCMSNotPreview, isPDFMode, examExport }) {
        if (!attachement.hasOwnProperty("svgData")) {
            return attachement;
        }

        if (isCMSNotPreview) {
            const regex = new RegExp("\/media\/([A-Za-z0-9-]{36}\\.[A-Za-z]+)", "gm");
            attachement.svgData = attachement.svgData.replace(regex, "/media/" + taskuuid + "/$1");
            if (angular.isDefined(attachement.svgFullData)) {
                attachement.svgFullData = attachement.svgFullData.replace(regex, "/media/" + taskuuid + "/$1");
            }
        } else {
            const regex = new RegExp("\/media\/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\/", "gm");
            attachement.svgData = attachement.svgData.replace(regex, "/media/");
            if (angular.isDefined(attachement.svgFullData)) {
                attachement.svgFullData = attachement.svgFullData.replace(regex, "/media/");
            }
        }

        if (isPDFMode || examExport) {
            const hrefRe = new RegExp("\\shref=", "gm");
            attachement.svgData = attachement.svgData.replace(hrefRe, " xmlns:xlink=\"http://www.w3.org/1999/xlink\" xlink:href=");
            if (angular.isDefined(attachement.svgFullData)) {
                attachement.svgFullData = attachement.svgFullData.replace(hrefRe, " xmlns:xlink=\"http://www.w3.org/1999/xlink\" xlink:href=");
            }
        }

        return attachement;
    }

    /**
     * Cleans SVG content for PDF export.
     * @param {string} orgSVG - The original SVG content.
     * @param {Object} $ - jQuery instance.
     * @returns {string} The cleaned SVG content.
     */
    cleanSVGForPDF(orgSVG, $) {
        const orgSVGElement = $(orgSVG);
        const svgElementsWithStrokeWidthZero = orgSVGElement.find("[stroke-width='0']");

        if (svgElementsWithStrokeWidthZero.length === 0) {
            return orgSVG;
        }

        svgElementsWithStrokeWidthZero.each(function () {
            const da = this.getAttribute('stroke-dasharray');
            if (typeof (da) === 'undefined' || da == null) {
                return;
            }
            this.removeAttribute("stroke-dasharray");
        });

        const xs = new XMLSerializer();
        let svg_xml = xs.serializeToString(orgSVGElement[0]);
        svg_xml = svg_xml.replace(/ href/g, ' xlink:href');

        return svg_xml;
    }

    /**
     * Normalizes SVG content for comparison.
     * @param {string} svg - The SVG content to normalize.
     * @param {Object} $ - jQuery instance.
     * @param {boolean} isCMSNotPreview - Whether in CMS not preview mode.
     * @returns {string} The normalized SVG content.
     */
    normaliseSVG(svg, $, isCMSNotPreview) {
        if (!svg || svg.length === 0) {
            return "";
        }
        svg = this.addUCFontsToSVG(svg);

        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(svg, "image/svg+xml");

            if (doc.getElementsByTagName("parsererror").length > 0) {
                return svg;
            }

            const serializer = new XMLSerializer();
            let normalized = serializer.serializeToString(doc.documentElement);
            normalized = normalized.replace(/>\s+</g, '><');

            return normalized;
        } catch (e) {
            console.warn("UniversalCanvasService: Error normalising SVG", e);
            return svg;
        }
    }
}

/**
 * History class manages the undo and redo functionality for a given object.
 */
class History {
    /**
     * Constructor for History.
     * @param {string} objectUUID - The UUID of the object.
     * @param {TaskDataService} TaskDataService - The fetch context function.
     */
    constructor(objectUUID, TaskDataService) {
        this._objectUUID = objectUUID;
        this._contentObject = TaskDataService.fetchContext().read(this._objectUUID);
        this._undoStackObject = null;
        this._timeout = null;
    }

    /**
     * Set the undo stack with the given data.
     * @param {string} undoStack - The undo stack data.
     * @param {string} backgroundImage - The background image.
     * @param {string} backgroundColor - The background color.
     * @param {Function} [callback] - Optional callback invoked after stack update (or noop if unchanged).
     */
    setUndoStack(undoStack, backgroundImage, backgroundColor, callback) {
        if (this._timeout) {
            clearTimeout(this._timeout);
        }
        this._timeout = setTimeout(() => {
            const data = {
                content: btoa(undoStack),
                backgroundImage: backgroundImage,
                backgroundColor: backgroundColor
            };

            if (!this._undoStackObject) {
                this._undoStackObject = {
                    index: 0,
                    undoStack: [data]
                };
            } else {
                if (this._undoStackObject.undoStack[this._undoStackObject.undoStack.length - 1] &&
                    btoa(undoStack) === this._undoStackObject.undoStack[this._undoStackObject.undoStack.length - 1].content) {
                    if (callback) callback();
                    return;
                }
                this._undoStackObject.index += 1;
                let index = this._undoStackObject.undoStack.length;

                do {
                    if (index >= this._undoStackObject.index) {
                        this._undoStackObject.undoStack.splice(index, 1);
                    }
                } while (index--);

                this._undoStackObject.undoStack.push(data);
            }

            if (callback) callback();
        }, 50);
    }

    /**
     * Get the undo stack.
     * @returns {Object} - The undo stack object.
     */
    getUndoStack() {
        return this._undoStackObject;
    }

    /**
     * Check if undo is allowed.
     * @returns {boolean} - True if undo is allowed, false otherwise.
     */
    allowUndo() {
        if (!this._undoStackObject) {
            return false;
        }
        return this._undoStackObject.index > 0;
    }

    /**
     * Check if redo is allowed.
     * @returns {boolean} - True if redo is allowed, false otherwise.
     */
    allowRedo() {
        if (!this._undoStackObject) {
            return false;
        }
        return this._undoStackObject.undoStack[this._undoStackObject.index + 1];
    }

    /**
     * Perform an undo operation.
     * @returns {Object|null} - The previous state or null if no previous state.
     */
    undo() {
        this._contentObject.flagAsDirty();
        if (this._undoStackObject.undoStack[this._undoStackObject.index - 1]) {
            this._undoStackObject.index -= 1;
            const data = this._undoStackObject.undoStack[this._undoStackObject.index];
            return {
                content: atob(data.content),
                backgroundImage: data.backgroundImage,
                backgroundColor: data.backgroundColor
            };
        }
        return null;
    }

    /**
     * Perform a redo operation.
     * @returns {Object|null} - The next state or null if no next state.
     */
    redo() {
        this._contentObject.flagAsDirty();
        if (this._undoStackObject.undoStack[this._undoStackObject.index + 1]) {
            this._undoStackObject.index += 1;
            const data = this._undoStackObject.undoStack[this._undoStackObject.index];
            return {
                content: atob(data.content),
                backgroundImage: data.backgroundImage,
                backgroundColor: data.backgroundColor
            };
        }
        return null;
    }
}

export default UniversalCanvasService;